import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';
import ProductCard from '../components/ProductCard';
import './styles/HomePage.css';

// ===== Extracted Components =====

const Hero: React.FC = () => (
  <section className="hero">
    <h1 className="hero-title font-serif">Welcome to Maple Nook</h1>
    <p className="hero-subtitle">Handcrafted and made with care</p>
  </section>
);

const CarouselButton: React.FC<{
  direction: 'prev' | 'next';
  onClick?: () => void;
  disabled?: boolean;
}> = ({ direction, onClick, disabled = false }) => (
  <button
    className={`carousel-button ${direction} ${disabled ? 'disabled' : ''}`}
    onClick={onClick}
    aria-label={direction === 'prev' ? 'Previous' : 'Next'}
    disabled={disabled}
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d={direction === 'prev' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
      />
    </svg>
  </button>
);

const SkeletonCard: React.FC = () => (
  <div className="carousel-slide">
    <div className="product-card skeleton-card">
      <div className="skeleton skeleton-image"></div>
      <div className="product-content">
        <div className="skeleton skeleton-category"></div>
        <div className="skeleton skeleton-product-name"></div>
        <div className="skeleton skeleton-description"></div>
        <div className="skeleton skeleton-description short"></div>
        <div className="product-footer">
          <div className="skeleton skeleton-price"></div>
          <div className="skeleton skeleton-button"></div>
        </div>
      </div>
    </div>
  </div>
);

// ===== Main Component =====

const HomePage: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Fetch first 6 products for carousel (featured products)
  const { products, loading, error } = useProducts({ limit: 6 });

  // Update current index based on scroll position
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleScroll = () => {
      const scrollLeft = track.scrollLeft;
      const slideWidth = track.offsetWidth;
      const newIndex = Math.round(scrollLeft / slideWidth);
      setCurrentIndex(newIndex);
    };

    track.addEventListener('scroll', handleScroll, { passive: true });
    return () => track.removeEventListener('scroll', handleScroll);
  }, [products.length]);

  const scrollToSlide = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    
    const slideWidth = track.offsetWidth;
    track.scrollTo({
      left: index * slideWidth,
      behavior: 'smooth'
    });
  };

  const nextSlide = () => {
    const newIndex = currentIndex === products.length - 1 ? 0 : currentIndex + 1;
    scrollToSlide(newIndex);
  };

  const prevSlide = () => {
    const newIndex = currentIndex === 0 ? products.length - 1 : currentIndex - 1;
    scrollToSlide(newIndex);
  };

  const goToSlide = (index: number) => {
    scrollToSlide(index);
  };

  // Loading state
  if (loading) {
    return (
      <div className="home-page">
        <Hero />
        <section className="carousel-section">
          <div className="skeleton skeleton-section-title"></div>
          <div className="carousel">
            <CarouselButton direction="prev" disabled />
            <div className="carousel-track">
              <SkeletonCard />
            </div>
            <CarouselButton direction="next" disabled />
          </div>
          <div className="carousel-dots">
            <span className="dot skeleton-indicator"></span>
            <span className="dot skeleton-indicator"></span>
            <span className="dot skeleton-indicator"></span>
          </div>
        </section>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="home-page">
        <Hero />
        <div className="error-container">
          <p className="error-message">Failed to load featured products: {error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No products
  if (products.length === 0) {
    return (
      <div className="home-page">
        <Hero />
        <div className="empty-state">
          <p>No featured products available at the moment.</p>
          <Link to="/products" className="browse-button">
            Browse All Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <Hero />

      {/* Featured Products Carousel */}
      <section className="carousel-section fade-in">
        <h2 className="section-title font-serif">Featured Products</h2>

        <div className="carousel">
          <CarouselButton direction="prev" onClick={prevSlide} />

          <div className="carousel-track" ref={trackRef}>
            {products.map((product) => (
              <div key={product.id} className="carousel-slide">
                <ProductCard
                  product={product}
                  variant="carousel"
                  descriptionLines={3}
                />
              </div>
            ))}
          </div>

          <CarouselButton direction="next" onClick={nextSlide} />
        </div>

        <div className="carousel-dots">
          {products.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Browse All Products CTA */}
      <section className="browse-all-section">
        <h2 className="browse-title font-serif">Discover More</h2>
        <p className="browse-subtitle">Explore our full collection of handcrafted treasures</p>
        <Link to="/products" className="browse-button">
          <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
          Browse All Products
        </Link>
      </section>
    </div>
  );
};

export default HomePage;